import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  TableContainer,
  Paper,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  TextField,
  Pagination,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  TableSortLabel,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  useMediaQuery,
  ButtonBase,
  Checkbox,
  FormControlLabel,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { Add, Edit, Visibility, Cancel } from '@mui/icons-material';
import axios from '../services/axios';

function DynamicList({
  entityName,
  fields,
  filter,
  label,
  edit,
  desabilitado,
  labelEdit,
  titulo,
  mostrarTitulo,
  defaultOrder,
  order
}) {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  // Converte as strings de props em arrays
  const fieldsArray = fields.split(',').map((f) => f.trim());
  const labelsArray = label ? label.split(',').map((f) => f.trim()) : [];
  const editFieldsArray = edit ? edit.split(',').map((f) => f.trim()) : [];
  const disabledFieldsArray = desabilitado
    ? desabilitado.split(',').map((f) => f.trim())
    : [];
  const labelEditArray = labelEdit
    ? labelEdit.split(',').map((f) => f.trim())
    : [];

  // Estados para listagem (dados) e paginação
  const [rows, setRows] = useState([]);
  const [totalElements, setTotalElements] = useState(0);
  const [page, setPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [searchValue, setSearchValue] = useState('');

  // Ordenação simples
  const [sortKey, setSortKey] = useState(null);
  const [sortDirection, setSortDirection] = useState('asc');

  // Modal
  const [openModal, setOpenModal] = useState(false);
  const [modalMode, setModalMode] = useState('edit'); // "edit" ou "view"
  const [editData, setEditData] = useState({});       // dados do registro selecionado

  // Confirmação de exclusão
  const [confirmDelete, setConfirmDelete] = useState({ open: false, rowIndex: null });

  // Tipos dos campos (String, Boolean, etc.) retornados pelo backend
  const [fieldTypes, setFieldTypes] = useState([]); 

  // Carregamos dados da listagem sempre que mudar paginação, busca ou ordenação
  useEffect(() => {
    fetchData();
    // eslint-disable-next-line
  }, [page, rowsPerPage, searchValue, sortKey, sortDirection]);

  // Buscar dados via endpoint
  const fetchData = async () => {
    try {
      const params = {
        entityName,
        fields,
        labels: label,
        filter,
        search: searchValue,
        page: page - 1,
        size: rowsPerPage,
        order: sortKey || defaultOrder,
        orderDirection:sortDirection|| order
      };
      if (sortKey) {
        params.sort = `${sortKey},${sortDirection}`;
      }

      const response = await axios.get('/api/dynamic-list', { params });
      /*
        Esperamos algo como:
        {
          content: [ [...], [...], ... ],
          totalElements: 123,
          fields: [...],
          labels: [...],
          fieldTypes: [...], // ex: ["Long","String","Boolean"]
          ...
        }
      */
      const data = response.data;
      setRows(data.content || []);
      setTotalElements(data.totalElements || 0);
      setFieldTypes(data.fieldTypes || []);
    } catch (error) {
      console.error('Erro ao buscar listagem dinâmica:', error);
    }
  };

  // Paginação
  const handleRowsPerPageChange = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(1);
  };
  const handlePageChange = (event, value) => {
    setPage(value);
  };

  // Filtro/busca
  const handleSearchChange = (event) => {
    setSearchValue(event.target.value);
    setPage(1);
  };

  // Ordenação
  const handleSort = (fieldName) => {
    if (sortKey === fieldName) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortKey(fieldName);
      setSortDirection('asc');
    }
  };

  // Converte uma row (Array) no objeto { campo: valor, ... }
  const mapRowToObject = (row) => {
    const obj = {};
    fieldsArray.forEach((field, idx) => {
      obj[field] = row[idx];
    });
    return obj;
  };

  // Ações do Modal:

  // 1) Novo
  const handleNew = () => {
    setModalMode('edit');
    // Cria objeto vazio (ou defaults) para cada campo em edit
    const newObj = {};
    editFieldsArray.forEach((field) => {
      newObj[field] = ''; 
    });
    setEditData(newObj);
    setOpenModal(true);
  };

  // 2) Editar
  const handleOpenEdit = (row) => {
    setModalMode('edit');
    const mapped = mapRowToObject(row);
    setEditData(mapped);
    setOpenModal(true);
  };

  // 3) Consultar
  const handleOpenView = (row) => {
    setModalMode('view');
    const mapped = mapRowToObject(row);
    setEditData(mapped);
    setOpenModal(true);
  };

  // Fechar modal
  const handleCloseModal = () => {
    setOpenModal(false);
    setEditData({});
  };

  // Quando usuário altera valor de um campo no modal
  const handleChangeEditField = (field, value) => {
    setEditData((prev) => ({
      ...prev,
      [field]: value
    }));
  };

  // Salvar (POST ou PUT)
  const handleSaveEdit = async () => {
    try {
      // Primary Key é o primeiro campo em editFieldsArray
      const primaryKeyField = editFieldsArray[0];
      const idValue = editData[primaryKeyField];

      // Montamos array de { fieldName, fieldValue }
      const body = Object.entries(editData).map(([k, v]) => ({
        fieldName: k,
        fieldValue: v
      }));

      if (!idValue) {
        // Se não temos ID, vamos criar (POST)
        await axios.post(`/api/dynamic-entity/${entityName}`, body);
      } else {
        // Se temos ID, vamos editar (PUT)
        await axios.put(`/api/dynamic-entity/${entityName}/${idValue}`, body);
      }

      handleCloseModal();
      fetchData();
    } catch (error) {
      console.error('Erro ao salvar genericamente:', error);
    }
  };

  // Exclusão
  const handleDelete = (rowIndex) => {
    setConfirmDelete({ open: true, rowIndex });
  };
  const handleCancelDelete = () => {
    setConfirmDelete({ open: false, rowIndex: null });
  };
  const handleConfirmDelete = () => {
    const row = rows[confirmDelete.rowIndex];
    const obj = mapRowToObject(row);
    const primaryKeyField = editFieldsArray[0];
    const idValue = obj[primaryKeyField];

    axios
      .delete(`/api/dynamic-entity/${entityName}/${idValue}`)
      .then(() => fetchData())
      .catch((err) => console.error('Erro ao excluir:', err))
      .finally(() => handleCancelDelete());
  };

  // Cabeçalhos da tabela
  const headers = fieldsArray.map((fieldName, i) => ({
    fieldName,
    label: labelsArray.length === fieldsArray.length ? labelsArray[i] : fieldName
  }));

  // Renderiza o input de acordo com o tipo do campo
  const renderFieldByType = (fieldName, fieldType, value, disabled, onChange) => {
    console.log(fieldName, fieldType, value, disabled, onChange)
    switch (fieldType) {
      case 'Boolean':
      case 'boolean':
        return (
          <FormControlLabel
            control={
              <Checkbox
                checked={!!value}
                onChange={(e) => onChange(fieldName, e.target.checked)}
                disabled={disabled}
              />
            }
            label=""
          />
        );
      case 'Integer':
      case 'Long':
      case 'Double':
      case 'Float':
        return (
          <TextField
            fullWidth
            size="small"
            type="number"
            disabled={disabled}
            value={value ?? ''}
            onChange={(e) => onChange(fieldName, e.target.value)}
          />
        );
      case 'Date':
      case 'LocalDate':
        return (
          <TextField
            fullWidth
            size="small"
            type="date"
            disabled={disabled}
            value={value ?? ''}
            onChange={(e) => onChange(fieldName, e.target.value)}
          />
        );
      default:
        return (
          <TextField
            fullWidth
            size="small"
            disabled={disabled}
            value={value ?? ''}
            onChange={(e) => onChange(fieldName, e.target.value)}
          />
        );
    }
  };

  return (
    <Box sx={{ padding: { xs: 1, sm: 2 } }}>
      {/* Título opcional */}
      {mostrarTitulo === 'true' && (
        <Typography variant="h4" align="center" sx={{ marginBottom: 2 }}>
          {titulo || entityName}
        </Typography>
      )}

       {/* Container superior para botão Novo, em uma linha separada */}
       <div  alignItems='center'>
        


    {/* Container para o campo de filtro, em outra linha */}
  <Box
    sx={{
        display: 'flex',
        justifyContent: 'center',
      mb: 2
    }}
  >
    <TextField
      variant="outlined"
      size="small"
      placeholder={`Filtrar por ${filter}...`}
      onChange={handleSearchChange}
      value={searchValue}
      fullWidth
      sx={{ maxWidth: 300 }}
    />
  </Box>   

    <Box
    sx={{
        display: 'flex',
        justifyContent: 'center',
      mb: 2
    }}
  >
    <Button
      variant="contained"
      color="primary"
      startIcon={<Add />}
      onClick={handleNew}
    >
      Novo
    </Button>
  </Box>
 

  
  </div>

      {/* Selecionar registros por página */}
      <Box
        sx={{
            display: 'flex',
            justifyContent: 'center',
          gap: 2,
          mb: 2
        }}
      >
        <FormControl sx={{ minWidth: 150 }}>
          <InputLabel>Registros por Página</InputLabel>
          <Select
            value={rowsPerPage}
            onChange={handleRowsPerPageChange}
            label="Registros por Página"
          >
            <MenuItem value={5}>5</MenuItem>
            <MenuItem value={10}>10</MenuItem>
            <MenuItem value={20}>20</MenuItem>
          </Select>
        </FormControl>
      </Box>

      {/* Tabela */}
      <TableContainer component={Paper}>
        <Table size="small">
          <TableHead>
            <TableRow>
              {headers.map((header, idx) => (
                <TableCell key={idx}>
                  <TableSortLabel
                    active={sortKey === header.fieldName}
                    direction={sortKey === header.fieldName ? sortDirection : 'asc'}
                    onClick={() => handleSort(header.fieldName)}
                  >
                    {header.label}
                  </TableSortLabel>
                </TableCell>
              ))}
              <TableCell>Ações</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.map((row, rIndex) => (
              <TableRow key={rIndex}>
                {row.map((cellValue, cIndex) => (
                  <TableCell key={cIndex}>
                    {String(cellValue ?? '')}
                  </TableCell>
                ))}
                <TableCell>
                  <Box
                    sx={{
                      display: 'flex',
                      flexDirection: 'row',
                      justifyContent: 'center',
                      alignItems: 'center',
                      gap: 2
                    }}
                  >
                    {/* Alterar */}
                    <ButtonBase onClick={() => handleOpenEdit(row)}>
                      <Edit fontSize="small" sx={{ color: 'orange' }} />
                      <Typography variant="caption" sx={{ ml: 0.5, color: 'orange' }}>
                        Alterar
                      </Typography>
                    </ButtonBase>

                    {/* Consultar */}
                    <ButtonBase onClick={() => handleOpenView(row)}>
                      <Visibility fontSize="small" sx={{ color: 'blue' }} />
                      <Typography variant="caption" sx={{ ml: 0.5, color: 'blue' }}>
                        Consultar
                      </Typography>
                    </ButtonBase>

                    {/* Excluir */}
                    <ButtonBase onClick={() => handleDelete(rIndex)}>
                      <Cancel fontSize="small" sx={{ color: 'red' }} />
                      <Typography variant="caption" sx={{ ml: 0.5, color: 'red' }}>
                        Excluir
                      </Typography>
                    </ButtonBase>
                  </Box>
                </TableCell>
              </TableRow>
            ))}

            {rows.length === 0 && (
              <TableRow>
                <TableCell colSpan={headers.length + 1} align="center">
                  Nenhum registro encontrado.
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Paginação */}
      <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
        <Pagination
          count={Math.ceil(totalElements / rowsPerPage)}
          page={page}
          onChange={handlePageChange}
          color="primary"
        />
      </Box>

      {/* Modal genérico: Novo / Editar / Consultar */}
      <Dialog open={openModal} onClose={handleCloseModal} fullWidth maxWidth="sm">
        <DialogTitle>
          {modalMode === 'view'
            ? 'Consultar'
            : editData[editFieldsArray[0]] ? 'Alterar' : 'Novo'}{' '}
          {titulo || entityName}
        </DialogTitle>
        <DialogContent dividers>
          {editFieldsArray.map((field, idx) => {
            // Label do campo
            const fieldLabel =
              labelEditArray.length === editFieldsArray.length
                ? labelEditArray[idx]
                : field;

            // Achar tipo do campo (se estiver em fieldsArray)
            const globalIndex = fieldsArray.indexOf(field);
            let fieldType = null;
            if (globalIndex !== -1) {
              fieldType = fieldTypes[globalIndex];
            }

            return (
              <Box key={field} sx={{ my: 1 }}>
                <Typography variant="subtitle2" sx={{ mb: 0.3 }}>
                  {fieldLabel}
                </Typography>
                {renderFieldByType(
                  field,
                  fieldType,
                  editData[field],
                  // se "view" ou campo desabilitado
                  modalMode === 'view' || disabledFieldsArray.includes(field),
                  handleChangeEditField
                )}
              </Box>
            );
          })}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseModal} color="primary">
            Fechar
          </Button>
          {modalMode === 'edit' && (
            <Button onClick={handleSaveEdit} color="success">
              Salvar
            </Button>
          )}
        </DialogActions>
      </Dialog>

      {/* Diálogo de confirmação de exclusão */}
      <Dialog
        open={confirmDelete.open}
        onClose={handleCancelDelete}
        fullWidth
        maxWidth="xs"
      >
        <DialogTitle>Confirmar Exclusão</DialogTitle>
        <DialogContent>
          Tem certeza que deseja excluir este item?
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCancelDelete}>Cancelar</Button>
          <Button onClick={handleConfirmDelete} color="error">
            Excluir
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export default DynamicList;
